import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useEvidenceList } from '../api/queries/evidence';
import { useProjects } from '../api/queries/projects';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { fmtDate } from '../utils/date';
import { ProjectOptions } from '../components/ProjectOptions';
import { SortableTh } from '../components/SortableTh';
import { TableScroll } from '../components/TableScroll';
import { useClientSort } from '../hooks/useClientSort';
import './EvidencePage.css';

const TYPE_LABEL: Record<string, string> = {
  mobile_device: 'Mobile device',
  computer: 'Computer',
  hard_drive: 'Hard drive',
  removable_media: 'Removable media',
  vehicle: 'Vehicle',
  cloud_data: 'Cloud data',
  document: 'Document',
  other: 'Other',
};

const STATUS_LABEL: Record<string, string> = {
  received: 'Received',
  imaging: 'Imaging',
  analyzing: 'Analyzing',
  released: 'Released',
  destroyed: 'Destroyed',
  archived: 'Archived',
};

type EvidenceSortKey =
  | 'coc'
  | 'project'
  | 'type'
  | 'make_model'
  | 'serial'
  | 'owner'
  | 'acquired'
  | 'status'
  | 'hash';

export function EvidencePage(): JSX.Element {
  const [projectFilter, setProjectFilter] = useState<number | undefined>(undefined);
  const { data: projectsData } = useProjects();
  const { data, isPending, error } = useEvidenceList(projectFilter);

  const projects = projectsData?.projects ?? [];
  const items = data?.evidence ?? [];
  const sort = useClientSort(items, (e, key: EvidenceSortKey) => {
    switch (key) {
      case 'coc': return e.chain_of_custody_number;
      case 'project': return e.project_case_number ?? `#${e.project_id}`;
      case 'type': return TYPE_LABEL[e.evidence_type] ?? e.evidence_type;
      case 'make_model': return e.make_model ?? '';
      case 'serial': return e.serial_number ?? '';
      case 'owner': return e.owner_name ?? '';
      case 'acquired': return e.acquired_date;
      case 'status': return STATUS_LABEL[e.evidence_status] ?? e.evidence_status;
      case 'hash': return e.hash_value ?? '';
      default: return '';
    }
  }, { key: 'acquired', dir: 'desc' });

  if (isPending) return <p className="ev-status">Loading evidence…</p>;
  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? "You don't have permission to view evidence."
        : 'Failed to load evidence.';
    return <p className="ev-status ev-error">{msg}</p>;
  }

  return (
    <section className="section-shell">
      <header className="section-header">
        <h2>Evidence</h2>
        <div className="ev-header-controls">
          <select
            className="form-input ev-filter"
            value={projectFilter ?? 0}
            onChange={(e) => {
              const v = Number(e.target.value);
              setProjectFilter(v > 0 ? v : undefined);
            }}
          >
            <option value={0}>All projects</option>
            <ProjectOptions projects={projects} />
          </select>
          <span className="ev-count">{items.length} record(s)</span>
          {can('evidence.write') && (
            <Link
              to={`/evidence/new${projectFilter ? `?project_id=${projectFilter}` : ''}`}
              className="btn-cyan-primary"
            >
              New intake
            </Link>
          )}
        </div>
      </header>

      {items.length === 0 ? (
        <div className="section-body">
          <div className="ev-empty">
            <p>No evidence records {projectFilter ? 'for this project' : 'yet'}.</p>
            <p className="ev-empty-meta">
              Intake via <code>POST /api/evidence</code>. Mobile capture
              flow + web intake form land in subsequent commits.
            </p>
          </div>
        </div>
      ) : (
        <div className="section-body section-body-flush">
          <TableScroll id="evidence">
            <table className="ev-table">
              <thead>
                <tr>
                  <SortableTh column="coc" label="COC #" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="project" label="Project" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="type" label="Type" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="make_model" label="Make / Model" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="serial" label="Serial" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="owner" label="Owner" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="acquired" label="Acquired" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="status" label="Status" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                  <SortableTh column="hash" label="Hash" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
                </tr>
              </thead>
              <tbody>
                {sort.sorted.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <Link to={`/evidence/${e.id}`} className="ev-row-link">
                        <code>{e.chain_of_custody_number}</code>
                      </Link>
                    </td>
                    <td><code>{e.project_case_number ?? `#${e.project_id}`}</code></td>
                    <td>{TYPE_LABEL[e.evidence_type] ?? e.evidence_type}</td>
                    <td>{e.make_model ?? '—'}</td>
                    <td>{e.serial_number ?? '—'}</td>
                    <td>{e.owner_name ?? '—'}</td>
                    <td title={new Date(e.acquired_date).toISOString()}>
                      {fmtDate(e.acquired_date)}
                    </td>
                    <td>
                      <span className={`ev-status-pill ev-status-${e.evidence_status}`}>
                        {STATUS_LABEL[e.evidence_status] ?? e.evidence_status}
                      </span>
                    </td>
                    <td>
                      {e.hash_value ? (
                        <code className="ev-hash" title={e.hash_value}>
                          {e.hash_value.slice(0, 8)}…
                        </code>
                      ) : (
                        <span className="ev-no-hash">not yet imaged</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
              </TableScroll>
        </div>
      )}
    </section>
  );
}
